// components/EnhancedAdminDashboard.js
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  orderBy,
  onSnapshot,
  where,
  addDoc,
  serverTimestamp,
  getDoc,
   writeBatch
} from "firebase/firestore";
import {
  getUserSettings,
  updateUserSettings,
  addPromoRate,
  addReferralBonus,
  calculateDepositStatsEnhanced,
  getMonthlyGrowthRate,
  updateMonthlyGrowthRate,
  calculateDepositStats
} from "../utils/growthCalculations";
import { getAllPendingReferrals } from "../utils/referralSystem";

const firestore = getFirestore();

// Enhanced User Management Modal with Better Error Handling
const UserManagementModal = ({ user, onClose, onSuccess }) => {
  const [userSettings, setUserSettings] = useState(null);
  
  // ✅ REPLACE THIS EXISTING formData useState:
  const [formData, setFormData] = useState({
    monthlyRate: '',
    referralBonus: '',
    adminFeePercentage: '',
    adminFeeFixed: '',
    rateChangeReason: '',
    feeReason: '' 
  });
  
  const [promoData, setPromoData] = useState({
    name: '',
    rate: '',
    startDate: '',
    endDate: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rates');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchUserSettings = async () => {
      if (user) {
        try {
          console.log("Fetching settings for user:", user.uid); // Debug log
          const settings = await getUserSettings(user.uid);
          console.log("Retrieved settings:", settings); // Debug log
          
          setUserSettings(settings);
          setFormData({
            monthlyRate: settings.monthlyRate || '',
            referralBonus: '',
            adminFeePercentage: settings.adminFeePercentage || '',
            adminFeeFixed: settings.adminFeeFixed || '',
            rateChangeReason: ''
          });
          setIsLoading(false);
        } catch (error) {
          console.error("Error fetching user settings:", error);
          setError(`Failed to load user settings: ${error.message}`);
          setIsLoading(false);
        }
      }
    };

    fetchUserSettings();
  }, [user]);

  const handleUpdateUserSettings = async () => {
    setIsUpdating(true);
    setError("");
    setSuccessMessage("");

    try {
      console.log("Updating user settings with data:", formData); // Debug log
      
      const updateData = {};
      
      // Only include fields that have values
      if (formData.monthlyRate !== '' && formData.monthlyRate !== null) {
        updateData.monthlyRate = parseFloat(formData.monthlyRate);
      }
      if (formData.adminFeePercentage !== '' && formData.adminFeePercentage !== null) {
        updateData.adminFeePercentage = parseFloat(formData.adminFeePercentage);
      }
      if (formData.adminFeeFixed !== '' && formData.adminFeeFixed !== null) {
        updateData.adminFeeFixed = parseFloat(formData.adminFeeFixed);
      }
      if (formData.rateChangeReason) {
        updateData.rateChangeReason = formData.rateChangeReason;
      }

      console.log("Sending update data:", updateData); // Debug log

      // Call the update function
      const result = await updateUserSettings(user.uid, updateData, "admin");
      console.log("Update result:", result); // Debug log

      if (result.success === false) {
        throw new Error(result.error || "Update failed");
      }
      
      // Refresh user settings
      const updatedSettings = await getUserSettings(user.uid);
      console.log("Updated settings:", updatedSettings); // Debug log
      setUserSettings(updatedSettings);
      
      // Update form data to reflect new values
      setFormData({
        monthlyRate: updatedSettings.monthlyRate || '',
        referralBonus: '',
        adminFeePercentage: updatedSettings.adminFeePercentage || '',
        adminFeeFixed: updatedSettings.adminFeeFixed || '',
        rateChangeReason: ''
      });
      
      setSuccessMessage(`Settings updated successfully for ${user.fullName}!`);
      onSuccess(`Settings updated for ${user.fullName}`);
      
      // Clear the reason field after successful update
      setFormData(prev => ({ ...prev, rateChangeReason: '' }));
      
    } catch (error) {
      console.error("Error updating user settings:", error);
      setError(`Failed to update settings: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddReferralBonus = async () => {
    if (!formData.referralBonus) {
      setError("Please enter a referral bonus amount");
      return;
    }
    
    setIsUpdating(true);
    setError("");
    setSuccessMessage("");
    
    try {
      console.log("Adding referral bonus:", formData.referralBonus); // Debug log
      
      const result = await addReferralBonus(user.uid, parseFloat(formData.referralBonus), "admin", "Admin bonus");
      console.log("Referral bonus result:", result); // Debug log
      
      if (result.success === false) {
        throw new Error(result.error || "Failed to add referral bonus");
      }
      
      // Refresh user settings
      const updatedSettings = await getUserSettings(user.uid);
      setUserSettings(updatedSettings);
      
      setSuccessMessage(`Referral bonus added for ${user.fullName}!`);
      onSuccess(`Referral bonus added for ${user.fullName}`);
      setFormData({ ...formData, referralBonus: '' });
      
    } catch (error) {
      console.error("Error adding referral bonus:", error);
      setError(`Failed to add referral bonus: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddPromo = async () => {
    if (!promoData.name || !promoData.rate || !promoData.startDate || !promoData.endDate) {
      setError("Please fill in all promo fields");
      return;
    }

    setIsUpdating(true);
    setError("");
    setSuccessMessage("");
    
    try {
      console.log("Adding promo:", promoData); // Debug log
      
      const result = await addPromoRate(user.uid, promoData, "admin");
      console.log("Promo result:", result); // Debug log
      
      if (result.success === false) {
        throw new Error(result.error || "Failed to add promo");
      }
      
      // Refresh user settings
      const updatedSettings = await getUserSettings(user.uid);
      setUserSettings(updatedSettings);
      
      setSuccessMessage(`Promo rate added for ${user.fullName}!`);
      onSuccess(`Promo rate added for ${user.fullName}`);
      setPromoData({ name: '', rate: '', startDate: '', endDate: '' });
      
    } catch (error) {
      console.error("Error adding promo rate:", error);
      setError(`Failed to add promo: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-center mt-2">Loading user settings...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Manage User: {user.fullName}</h2>
            <p className="text-gray-600">{user.email}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Error and Success Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <strong>Error:</strong> {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            <strong>Success:</strong> {successMessage}
          </div>
        )}

        {/* Current Settings Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-blue-800 font-semibold text-sm">Current Rate</h3>
            <p className="text-blue-900 text-xl font-bold">
              {userSettings?.monthlyRate || 'Global'}%
              {userSettings?.isUsingGlobalRate && <span className="text-xs text-blue-600"> (Global)</span>}
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-green-800 font-semibold text-sm">Referral Bonus</h3>
            <p className="text-green-900 text-xl font-bold">+{userSettings?.referralBonus || 0}%</p>
            <p className="text-green-600 text-xs">{userSettings?.totalReferrals || 0} referrals</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-yellow-800 font-semibold text-sm">Admin Fees</h3>
            <p className="text-yellow-900 text-lg font-bold">
              {userSettings?.adminFeePercentage || 0}% + ${userSettings?.adminFeeFixed || 0}
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="text-purple-800 font-semibold text-sm">Active Promos</h3>
            <p className="text-purple-900 text-xl font-bold">{userSettings?.promoRates?.length || 0}</p>
          </div>
        </div>

        {/* Debug Information */}
        <div className="mb-4 p-3 bg-gray-100 rounded-lg text-xs">
          <strong>Debug Info:</strong> User ID: {user.uid} | Settings Loaded: {userSettings ? 'Yes' : 'No'} | 
          Form Rate: {formData.monthlyRate} | Current Rate: {userSettings?.monthlyRate}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {['rates', 'promos', 'referrals', 'fees', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2 font-medium capitalize ${
                activeTab === tab
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content - Rates */}
        {activeTab === 'rates' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Monthly Rate (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.monthlyRate}
                  onChange={(e) => setFormData({ ...formData, monthlyRate: e.target.value })}
                  className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Leave empty to use global rate"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Current: {userSettings?.monthlyRate || 'Global'}%
                </p>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Reason for Change
                </label>
                <input
                  type="text"
                  value={formData.rateChangeReason}
                  onChange={(e) => setFormData({ ...formData, rateChangeReason: e.target.value })}
                  className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Special rate, VIP client, etc."
                />
              </div>
            </div>
            <button
              onClick={handleUpdateUserSettings}
              disabled={isUpdating}
              className="px-6 py-3 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              {isUpdating ? "Updating..." : "Update Rate"}
            </button>
          </div>
        )}

        {/* History Tab with Better Display */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Rate Change History</h3>
            <div className="mb-4 p-3 bg-blue-100 rounded-lg text-sm">
              <strong>Debug:</strong> History entries: {userSettings?.rateHistory?.length || 0}
            </div>
            {userSettings?.rateHistory?.length > 0 ? (
              <div className="space-y-3">
                {userSettings.rateHistory.map((change, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">
                          Rate changed from {change.previousRate}% to {change.newRate}%
                        </p>
                        <p className="text-sm text-gray-600">Reason: {change.reason}</p>
                        <p className="text-xs text-gray-500">
                          By: {change.changedBy} on {change.changedAt?.toDate?.()?.toLocaleString() || 'Unknown date'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No rate changes recorded</p>
            )}
          </div>
        )}

{/* Tab Content - Promos */}
{activeTab === 'promos' && (
  <div className="space-y-6">
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800">Add Promo Period</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-2">
            Promo Name
          </label>
          <input
            type="text"
            value={promoData.name}
            onChange={(e) => setPromoData({ ...promoData, name: e.target.value })}
            className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Welcome Bonus, Holiday Special"
          />
        </div>
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-2">
            Promo Rate (%)
          </label>
          <input
            type="number"
            step="0.1"
            value={promoData.rate}
            onChange={(e) => setPromoData({ ...promoData, rate: e.target.value })}
            className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., 20"
          />
        </div>
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-2">
            Start Date
          </label>
          <input
            type="date"
            value={promoData.startDate}
            onChange={(e) => setPromoData({ ...promoData, startDate: e.target.value })}
            className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-2">
            End Date
          </label>
          <input
            type="date"
            value={promoData.endDate}
            onChange={(e) => setPromoData({ ...promoData, endDate: e.target.value })}
            className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      <button
        onClick={handleAddPromo}
        disabled={isUpdating || !promoData.name || !promoData.rate || !promoData.startDate || !promoData.endDate}
        className="px-6 py-3 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors disabled:opacity-50"
      >
        {isUpdating ? "Adding..." : "Add Promo"}
      </button>
    </div>

    {/* Existing Promos */}
    {userSettings?.promoRates?.length > 0 && (
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Existing Promos</h3>
        <div className="space-y-3">
          {userSettings.promoRates.map((promo) => (
            <div key={promo.id} className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold text-purple-800">{promo.name}</h4>
                  <p className="text-purple-600">Rate: {promo.rate}%</p>
                  <p className="text-sm text-purple-500">
                    {new Date(promo.startDate).toLocaleDateString()} - 
                    {new Date(promo.endDate).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${
                  new Date() < new Date(promo.endDate)
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {new Date() < new Date(promo.endDate) ? 'Active' : 'Expired'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
)}

{/* Enhanced Referral Processing Section */}
{activeTab === 'referrals' && (
  <div className="space-y-6">
    {/* Add Individual Referral Bonus */}
    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
      <h4 className="font-semibold text-blue-800 mb-3">Add Individual Referral Bonus</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-2">
            Add Referral Bonus (%)
          </label>
          <div className="flex gap-3">
            <input
              type="number"
              step="0.1"
              value={formData.referralBonus}
              onChange={(e) => setFormData({ ...formData, referralBonus: e.target.value })}
              className="flex-1 p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 0.5"
            />
            <button
              onClick={handleAddReferralBonus}
              disabled={isUpdating || !formData.referralBonus}
              className="px-6 py-3 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:opacity-50"
            >
              {isUpdating ? "Adding..." : "Add"}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            This will be added to their existing bonus
          </p>
        </div>
      </div>
    </div>

    {/* Enhanced Process Pending Referrals */}
    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
      <h4 className="font-semibold text-green-800 mb-3">🔄 Process Referral Bonuses</h4>
      <p className="text-sm text-green-700 mb-4">
        Check for new referrals and apply bonuses automatically. Each successful referral earns 0.5% bonus.
      </p>
      
      {/* Show Pending Referrals Count */}
     

{/* Show Pending Referrals Count */}
<div className="mb-4">
  <button
    onClick={async () => {
      setIsUpdating(true);
      try {
        // Check pending referrals for this specific user
        const investorsRef = collection(firestore, "Investors");
        const q = query(
          investorsRef, 
          where("referredBy", "==", user.uid),
          where("referralBonusProcessed", "==", false)
        );
        const referredUsers = await getDocs(q);
        
        if (referredUsers.empty) {
          setSuccessMessage('No new referrals to process for this user');
          setIsUpdating(false);
          return;
        }
        
        let bonusesToAdd = 0;
        let unprocessedCount = 0;
        const userIds = [];
        
        referredUsers.forEach(doc => {
          const data = doc.data();
          bonusesToAdd += 0.5; // 0.5% per referral
          unprocessedCount++;
          userIds.push(doc.id);
          console.log("Found unprocessed referral:", {
            userId: doc.id,
            email: data.emailAddress || data.email,
            fullName: data.fullName,
            referralDate: data.referralDate
          });
        });
        
        if (bonusesToAdd > 0) {
          // Add referral bonus to the referrer
          const result = await addReferralBonus(
            user.uid, 
            bonusesToAdd, 
            "admin", 
            `Processed ${unprocessedCount} referrals`
          );
          
          if (result.success) {
            // ✅ Update referralStats in the Investors document
            const userRef = doc(firestore, "Investors", user.uid);
            const userDoc = await getDoc(userRef);
            
            if (userDoc.exists()) {
              const currentStats = userDoc.data().referralStats || {
                totalReferrals: 0,
                successfulReferrals: 0,
                totalBonusEarned: 0
              };
              
              // Update the referral stats
              await updateDoc(userRef, {
                "referralStats.totalReferrals": currentStats.totalReferrals + unprocessedCount,
                "referralStats.successfulReferrals": currentStats.successfulReferrals + unprocessedCount,
                "referralStats.totalBonusEarned": (currentStats.totalBonusEarned || 0) + bonusesToAdd,
                "referralStats.lastUpdated": new Date()
              });
            }
            
            // Mark referrals as processed using batch
            const batch = writeBatch(firestore);
            userIds.forEach(userId => {
              const userRef = doc(firestore, "Investors", userId);
              batch.update(userRef, { 
                referralBonusProcessed: true,
                referralProcessedDate: new Date(),
                referralProcessingStatus: "completed"
              });
            });
            await batch.commit();
            
            // Refresh user settings
            const updatedSettings = await getUserSettings(user.uid);
            setUserSettings(updatedSettings);
            setSuccessMessage(`✅ Processed ${unprocessedCount} referrals, added ${bonusesToAdd}% bonus!`);
          } else {
            throw new Error(result.error || "Failed to add referral bonus");
          }
        }
      } catch (error) {
        console.error("Error processing referrals:", error);
        setError(`Failed to process referrals: ${error.message}`);
      } finally {
        setIsUpdating(false);
      }
    }}
    disabled={isUpdating}
    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50 mr-3"
  >
    {isUpdating ? "Processing..." : "🔄 Process This User's Referrals"}
  </button>
  
  {/* Process All Pending Referrals (Global) */}
  <button
    onClick={async () => {
      if (!window.confirm('Process ALL pending referrals for ALL users? This will apply bonuses to everyone who has unprocessed referrals.')) {
        return;
      }
      
      setIsUpdating(true);
      try {
        // Get all pending referrals across all users
        const allPendingReferrals = await getAllPendingReferrals();
        
        if (allPendingReferrals.length === 0) {
          setSuccessMessage('No pending referrals found across all users');
          setIsUpdating(false);
          return;
        }
        
        // Group by referrer
        const referralsByReferrer = {};
        allPendingReferrals.forEach(referral => {
          if (!referralsByReferrer[referral.referredBy]) {
            referralsByReferrer[referral.referredBy] = [];
          }
          referralsByReferrer[referral.referredBy].push(referral);
        });
        
        let totalProcessed = 0;
        let totalBonusAdded = 0;
        
        // Process each referrer's bonuses
        for (const [referrerId, referrals] of Object.entries(referralsByReferrer)) {
          const bonusAmount = referrals.length * 0.5; // 0.5% per referral
          
          // Add bonus to referrer
          const result = await addReferralBonus(
            referrerId,
            bonusAmount,
            "admin",
            `Global processing: ${referrals.length} referrals`
          );
          
          if (result.success) {
            // ✅ Update referralStats in the Investors document
            const referrerRef = doc(firestore, "Investors", referrerId);
            const referrerDoc = await getDoc(referrerRef);
            
            if (referrerDoc.exists()) {
              const currentStats = referrerDoc.data().referralStats || {
                totalReferrals: 0,
                successfulReferrals: 0,
                totalBonusEarned: 0
              };
              
              // Update the referral stats
              await updateDoc(referrerRef, {
                "referralStats.totalReferrals": currentStats.totalReferrals + referrals.length,
                "referralStats.successfulReferrals": currentStats.successfulReferrals + referrals.length,
                "referralStats.totalBonusEarned": (currentStats.totalBonusEarned || 0) + bonusAmount,
                "referralStats.lastUpdated": new Date()
              });
            }
            
            // Mark all referrals as processed
            const batch = writeBatch(firestore);
            referrals.forEach(referral => {
              const userRef = doc(firestore, "Investors", referral.userId);
              batch.update(userRef, {
                referralBonusProcessed: true,
                referralProcessedDate: new Date(),
                referralProcessingStatus: "completed"
              });
            });
            await batch.commit();
            
            totalProcessed += referrals.length;
            totalBonusAdded += bonusAmount;
          }
        }
        
        setSuccessMessage(`🎉 Global processing complete! Processed ${totalProcessed} referrals, added ${totalBonusAdded}% total bonus across ${Object.keys(referralsByReferrer).length} users.`);
        
        // Refresh current user settings if they were affected
        if (referralsByReferrer[user.uid]) {
          const updatedSettings = await getUserSettings(user.uid);
          setUserSettings(updatedSettings);
        }
        
      } catch (error) {
        console.error("Error in global referral processing:", error);
        setError(`Global processing failed: ${error.message}`);
      } finally {
        setIsUpdating(false);
      }
    }}
    disabled={isUpdating}
    className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors disabled:opacity-50"
  >
    🌍 Process ALL Pending Referrals
  </button>
</div>
    </div>

    {/* Current Referral Status */}
    <div className="bg-green-50 p-4 rounded-lg">
      <h4 className="font-semibold text-green-800 mb-2">Current Referral Status</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-green-700">
            <strong>Total Bonus:</strong> +{userSettings?.referralBonus || 0}%
          </p>
          <p className="text-green-700">
            <strong>Total Referrals:</strong> {userSettings?.totalReferrals || 0}
          </p>
        </div>
        <div>
          <p className="text-green-700">
            <strong>Referral Code:</strong> 
            <code className="ml-2 px-2 py-1 bg-green-100 rounded text-green-800">
              {userSettings?.referralCode || 'Not set'}
            </code>
          </p>
        </div>
      </div>
      
      {userSettings?.lastReferralBonus && (
        <div className="mt-3 p-3 bg-green-100 rounded">
          <p className="text-sm text-green-600">
            <strong>Last bonus:</strong> +{userSettings.lastReferralBonus.amount}%
          </p>
          <p className="text-xs text-green-500">
            Reason: {userSettings.lastReferralBonus.reason || 'No reason provided'}
          </p>
          <p className="text-xs text-green-500">
            Added: {userSettings.lastReferralBonus.addedAt ? 
              new Date(userSettings.lastReferralBonus.addedAt).toLocaleDateString() : 'Unknown'}
          </p>
        </div>
      )}
    </div>

    {/* Check for Existing Unprocessed Referrals */}
    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
      <h4 className="font-semibold text-yellow-800 mb-2">🔍 Debug: Check Referral Status</h4>
      <button
        onClick={async () => {
          setIsUpdating(true);
          try {
            // Check for users referred by this user
            const investorsRef = collection(firestore, "Investors");
            const q = query(investorsRef, where("referredBy", "==", user.uid));
            const snapshot = await getDocs(q);
            
            console.log("=== REFERRAL DEBUG INFO ===");
            console.log("Checking referrals for user:", user.uid, user.fullName);
            console.log("Total users referred by this user:", snapshot.size);
            
            const allReferrals = [];
            const unprocessedReferrals = [];
            
            snapshot.forEach(doc => {
              const data = doc.data();
              const referralInfo = {
                userId: doc.id,
                email: data.emailAddress || data.email,
                fullName: data.fullName,
                referralDate: data.referralDate,
                bonusProcessed: data.referralBonusProcessed,
                processingStatus: data.referralProcessingStatus
              };
              
              allReferrals.push(referralInfo);
              
              if (data.referralBonusProcessed !== true) {
                unprocessedReferrals.push(referralInfo);
              }
              
              console.log("Referred user:", referralInfo);
            });
            
            console.log("Unprocessed referrals:", unprocessedReferrals);
            console.log("=== END DEBUG INFO ===");
            
            setSuccessMessage(`Debug: Found ${allReferrals.length} total referrals, ${unprocessedReferrals.length} unprocessed. Check console for details.`);
            
          } catch (error) {
            console.error("Debug check failed:", error);
            setError(`Debug check failed: ${error.message}`);
          } finally {
            setIsUpdating(false);
          }
        }}
        disabled={isUpdating}
        className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors disabled:opacity-50 text-sm"
      >
        {isUpdating ? "Checking..." : "🔍 Debug Referral Status"}
      </button>
      <p className="text-xs text-yellow-600 mt-2">
        Use this to debug referral issues. Check browser console for detailed logs.
      </p>
    </div>

    {/* Fix Existing Users (One-time operation) */}
    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
      <h4 className="font-semibold text-red-800 mb-2">⚠️ Fix Legacy Referrals</h4>
      <p className="text-sm text-red-700 mb-3">
        One-time operation to fix users who were referred before the system update.
        This will set referralBonusProcessed: false for users who have referredBy but no bonus processing status.
      </p>
      <button
        onClick={async () => {
          if (!window.confirm('This will update ALL users who have referredBy but no referralBonusProcessed field. Continue?')) {
            return;
          }
          
          setIsUpdating(true);
          try {
            const investorsRef = collection(firestore, "Investors");
            const q = query(investorsRef, where("referredBy", "!=", null));
            const snapshot = await getDocs(q);
            
            let fixedCount = 0;
            const batch = writeBatch(firestore);
            
            snapshot.forEach(doc => {
              const data = doc.data();
              // If user has referredBy but no referralBonusProcessed field
              if (data.referredBy && data.referralBonusProcessed === undefined) {
                batch.update(doc.ref, {
                  referralBonusProcessed: false,
                  referralProcessingStatus: "pending",
                  needsLegacyFix: true
                });
                fixedCount++;
              }
            });
            
            if (fixedCount > 0) {
              await batch.commit();
              setSuccessMessage(`✅ Fixed ${fixedCount} legacy referral records. They can now be processed normally.`);
            } else {
              setSuccessMessage('No legacy referrals found that need fixing.');
            }
            
          } catch (error) {
            console.error("Error fixing legacy referrals:", error);
            setError(`Failed to fix legacy referrals: ${error.message}`);
          } finally {
            setIsUpdating(false);
          }
        }}
        disabled={isUpdating}
        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 text-sm"
      >
        {isUpdating ? "Fixing..." : "🔧 Fix Legacy Referrals"}
      </button>
    </div>

    {/* Reset Referral Bonus */}
    <div className="border-t pt-4">
      <h4 className="font-semibold text-gray-800 mb-2">Reset Referral Bonus</h4>
      <p className="text-sm text-gray-600 mb-3">
        This will reset the user's referral bonus to 0% and referral count to 0.
      </p>
      <button
        onClick={async () => {
          if (window.confirm('Are you sure you want to reset this user\'s referral bonus?')) {
            setIsUpdating(true);
            try {
              await updateUserSettings(user.uid, { 
                referralBonus: 0, 
                totalReferrals: 0,
                lastReferralBonus: null 
              }, "admin");
              const updatedSettings = await getUserSettings(user.uid);
              setUserSettings(updatedSettings);
              setSuccessMessage('Referral bonus reset successfully!');
            } catch (error) {
              setError(`Failed to reset referral bonus: ${error.message}`);
            } finally {
              setIsUpdating(false);
            }
          }
        }}
        disabled={isUpdating}
        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 text-sm"
      >
        Reset Referral Bonus
      </button>
    </div>
  </div>
)}

{/* Tab Content - Fees */}
{activeTab === 'fees' && (
  <div className="space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-gray-700 text-sm font-medium mb-2">
          Admin Fee Percentage (%)
        </label>
        <input
          type="number"
          step="0.1"
          min="0"
          value={formData.adminFeePercentage}
          onChange={(e) => setFormData({ ...formData, adminFeePercentage: e.target.value })}
          className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Percentage of returns"
        />
        <p className="text-xs text-gray-500 mt-1">
          Current: {userSettings?.adminFeePercentage || 0}%
        </p>
      </div>
      <div>
        <label className="block text-gray-700 text-sm font-medium mb-2">
          Fixed Monthly Fee ($)
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={formData.adminFeeFixed}
          onChange={(e) => setFormData({ ...formData, adminFeeFixed: e.target.value })}
          className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Fixed amount per month"
        />
        <p className="text-xs text-gray-500 mt-1">
          Current: ${userSettings?.adminFeeFixed || 0}
        </p>
      </div>
    </div>

    <div>
      <label className="block text-gray-700 text-sm font-medium mb-2">
        Fee Description (Optional)
      </label>
      <textarea
        value={formData.feeReason || ''}
        onChange={(e) => setFormData({ ...formData, feeReason: e.target.value })}
        className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        rows="2"
        placeholder="Reason for admin fees (e.g., Premium account management)"
      />
    </div>

    <button
      onClick={handleUpdateUserSettings}
      disabled={isUpdating}
      className="px-6 py-3 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors disabled:opacity-50"
    >
      {isUpdating ? "Updating..." : "Update Fees"}
    </button>

    {/* Fee Information */}
    <div className="bg-orange-50 p-4 rounded-lg">
      <h4 className="font-semibold text-orange-800 mb-2">Fee Structure Explanation</h4>
      <ul className="text-sm text-orange-700 space-y-1">
        <li><strong>Percentage Fee:</strong> Applied to monthly returns (e.g., 2% of profits)</li>
        <li><strong>Fixed Fee:</strong> Charged monthly regardless of performance</li>
        <li><strong>Combined:</strong> Both fees can be applied to the same user</li>
        <li><strong>Calculation:</strong> Fees are deducted from monthly growth automatically</li>
      </ul>
      
      {(userSettings?.adminFeePercentage > 0 || userSettings?.adminFeeFixed > 0) && (
        <div className="mt-3 p-2 bg-orange-100 rounded">
          <p className="text-sm font-semibold text-orange-800">Current Fee Structure:</p>
          <p className="text-sm text-orange-700">
            {userSettings.adminFeePercentage}% of returns + ${userSettings.adminFeeFixed} monthly
          </p>
          {userSettings.feeReason && (
            <p className="text-xs text-orange-600 mt-1">
              Reason: {userSettings.feeReason}
            </p>
          )}
        </div>
      )}
    </div>

    {/* Remove Fees */}
    <div className="border-t pt-4">
      <h4 className="font-semibold text-gray-800 mb-2">Remove All Fees</h4>
      <p className="text-sm text-gray-600 mb-3">
        This will remove all admin fees for this user.
      </p>
      <button
        onClick={async () => {
          if (window.confirm('Are you sure you want to remove all fees for this user?')) {
            setIsUpdating(true);
            try {
              await updateUserSettings(user.uid, { 
                adminFeePercentage: 0, 
                adminFeeFixed: 0,
                feeReason: null 
              }, "admin");
              const updatedSettings = await getUserSettings(user.uid);
              setUserSettings(updatedSettings);
              setFormData({
                ...formData,
                adminFeePercentage: '',
                adminFeeFixed: '',
                feeReason: ''
              });
              setSuccessMessage('Admin fees removed successfully!');
            } catch (error) {
              setError(`Failed to remove fees: ${error.message}`);
            } finally {
              setIsUpdating(false);
            }
          }
        }}
        disabled={isUpdating}
        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 text-sm"
      >
        Remove All Fees
      </button>
    </div>
  </div>
)}
      </motion.div>
    </motion.div>
  );
};

// Create Deposit Form Component (Enhanced)
const CreateDepositForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    userEmail: "",
    amount: "",
    currency: "₦",
    description: "",
    paymentMethod: "",
    transactionId: "",
    adminNotes: ""
  });
  const [users, setUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersCollection = collection(firestore, "Investors");
        const snapshot = await getDocs(usersCollection);
        const usersData = [];
        snapshot.forEach((doc) => {
          const userData = doc.data();
          usersData.push({
            uid: doc.id,
            email: userData.emailAddress || userData.email,
            fullName: userData.fullName
          });
        });
        setUsers(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      // Find user by email
      const selectedUser = users.find(user => user.email === formData.userEmail);
      if (!selectedUser) {
        throw new Error("User not found");
      }

      const depositsCollection = collection(firestore, "Deposits");
      
      const newDeposit = {
        userId: selectedUser.uid,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        status: "confirmed",
        createdAt: serverTimestamp(),
        confirmedAt: serverTimestamp(),
        confirmedBy: "admin",
        description: formData.description,
        paymentMethod: formData.paymentMethod,
        transactionId: formData.transactionId,
        metadata: {
          createdByAdmin: true,
          adminNotes: formData.adminNotes,
          enhancedSystem: true
        }
      };

      await addDoc(depositsCollection, newDeposit);
      onSuccess("Deposit created successfully!");
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-xl p-6 w-full max-w-md border shadow-lg max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Create New Deposit</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 p-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              User Email
            </label>
            <select
              value={formData.userEmail}
              onChange={(e) => setFormData({ ...formData, userEmail: e.target.value })}
              className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select User</option>
              {users.map((user) => (
                <option key={user.uid} value={user.email}>
                  {user.fullName} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Amount
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Currency
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="₦">₦ (Nigerian Naira)</option>
                <option value="$">$ (US Dollar)</option>
                <option value="€">€ (Euro)</option>
                <option value="£">£ (British Pound)</option>
                <option value="BTC">BTC (Bitcoin)</option>
                <option value="ETH">ETH (Ethereum)</option>
                <option value="USDT">USDT (Tether)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Bank transfer, Crypto deposit"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Payment Method
              </label>
              <input
                type="text"
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Bank, Crypto, etc."
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Transaction ID
              </label>
              <input
                type="text"
                value={formData.transactionId}
                onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
                className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Optional"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Admin Notes
            </label>
            <textarea
              value={formData.adminNotes}
              onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
              className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="2"
              placeholder="Internal notes (optional)"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Creating..." : "Create Deposit"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Enhanced Admin Dashboard Main Component
const EnhancedAdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [globalRate, setGlobalRate] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showWithdrawals, setShowWithdrawals] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [processingWithdrawal, setProcessingWithdrawal] = useState(null);
  const [stats, setStats] = useState({
    totalDeposits: 0,
    totalValue: 0,
    totalGrowth: 0,
    activeUsers: 0
  });

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users
        const usersCollection = collection(firestore, "Investors");
        const usersSnapshot = await getDocs(usersCollection);
        const usersData = [];
        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          usersData.push({
            uid: doc.id,
            email: userData.emailAddress || userData.email,
            fullName: userData.fullName,
            currency: userData.currency || "₦"
          });
        });
        setUsers(usersData);

        // Fetch global rate
        const rate = await getMonthlyGrowthRate();
        setGlobalRate(rate);

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch deposits
  useEffect(() => {
    const depositsCollection = collection(firestore, "Deposits");
    const q = query(depositsCollection, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const depositsData = [];
      snapshot.forEach((doc) => {
        depositsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setDeposits(depositsData);

      // Calculate enhanced stats
      await calculateEnhancedStats(depositsData);
    });

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalRate]);

  // Fetch withdrawals
  useEffect(() => {
    const withdrawalsCollection = collection(firestore, "Withdrawals");
    const q = query(withdrawalsCollection, orderBy("requestedAt", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const withdrawalsData = [];
      snapshot.forEach((doc) => {
        withdrawalsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setWithdrawals(withdrawalsData);
    });

    return () => unsubscribe();
  }, []);

  const calculateEnhancedStats = async (depositsData) => {
    // Filter out deposits without valid createdAt timestamps
    const validDeposits = depositsData.filter(d => 
      d.status === 'confirmed' && 
      d.createdAt && 
      d.amount != null
    );
    
    const totalDeposits = validDeposits.reduce((sum, d) => sum + (d.amount || 0), 0);
    
    let totalCurrentValue = 0;
    
    // Use enhanced calculations for users with custom settings, fallback to legacy for others
    for (const deposit of validDeposits) {
      try {
        if (deposit.userId) {
          // Try enhanced calculation first
          const enhancedStats = await calculateDepositStatsEnhanced(deposit);
          totalCurrentValue += enhancedStats.currentValue;
        } else {
          // Fallback to legacy calculation
          const legacyStats = calculateDepositStats(deposit, globalRate);
          totalCurrentValue += legacyStats.currentValue;
        }
      } catch (error) {
        console.error('Error calculating stats for deposit:', deposit.id, error);
        // Fallback to legacy calculation
        const legacyStats = calculateDepositStats(deposit, globalRate);
        totalCurrentValue += legacyStats.currentValue;
      }
    }

    const activeUsers = new Set(validDeposits.map(d => d.userId)).size;

    setStats({
      totalDeposits,
      totalValue: totalCurrentValue,
      totalGrowth: totalCurrentValue - totalDeposits,
      activeUsers
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 5000);
  };

  const updateGlobalRate = async (newRate) => {
    try {
      await updateMonthlyGrowthRate(newRate, "admin");
      setGlobalRate(newRate);
      handleSuccess("Global rate updated successfully!");
    } catch (error) {
      console.error("Error updating global rate:", error);
    }
  };

  // Withdrawal processing functions
  const handleWithdrawalAction = async (withdrawalId, action, adminNotes = "") => {
    setProcessingWithdrawal(withdrawalId);
    
    try {
      const withdrawalRef = doc(firestore, "Withdrawals", withdrawalId);
      await updateDoc(withdrawalRef, {
        status: action,
        processedAt: serverTimestamp(),
        processedBy: "admin",
        adminNotes: adminNotes,
        processedDate: new Date().toISOString()
      });
      
      handleSuccess(`Withdrawal ${action} successfully!`);
    } catch (error) {
      console.error("Error updating withdrawal:", error);
      handleSuccess(`Error: ${error.message}`);
    } finally {
      setProcessingWithdrawal(null);
    }
  };

  const getWithdrawalStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'completed': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading enhanced admin dashboard...</p>
        </div>
      </div>
    );
  }

  const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending').length;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">🚀 Enhanced Admin Dashboard</h1>
          <p className="text-gray-600">Manage individual user rates, promos, referrals, and fees</p>
        </div>

        {successMessage && (
          <motion.div
            className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {successMessage}
          </motion.div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border shadow-lg">
            <h3 className="text-gray-600 text-sm">Total Invested</h3>
            <p className="text-gray-800 text-xl font-bold">$ {stats.totalDeposits.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-lg">
            <h3 className="text-gray-600 text-sm">Current Value</h3>
            <p className="text-green-600 text-xl font-bold">$ {stats.totalValue.toFixed(2).toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-lg">
            <h3 className="text-gray-600 text-sm">Total Growth</h3>
            <p className="text-blue-600 text-xl font-bold">$ {stats.totalGrowth.toFixed(2).toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-lg">
            <h3 className="text-gray-600 text-sm">Active Users</h3>
            <p className="text-purple-600 text-xl font-bold">{stats.activeUsers}</p>
          </div>
        </div>

        {/* Global Settings */}
        <div className="bg-white rounded-xl p-6 border shadow-lg mb-8">
          <h3 className="text-lg font-bold text-gray-800 mb-4">🌍 Global Settings</h3>
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Default Monthly Rate (%)
              </label>
              <input
                type="number"
                step="0.1"
                value={globalRate}
                onChange={(e) => setGlobalRate(parseFloat(e.target.value) || 10)}
                className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => updateGlobalRate(globalRate)}
              className="mt-6 px-6 py-3 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Update Global Rate
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            This rate applies to all users without custom settings. Current users with custom rates won't be affected.
          </p>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border shadow-lg">
            <h3 className="text-lg font-bold text-gray-800 mb-4">💰 Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => setShowCreateForm(true)}
                className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
              >
                <span>➕</span>
                Create New Deposit
              </button>
              <button
                onClick={() => setShowWithdrawals(!showWithdrawals)}
                className="w-full px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
              >
                <span>💸</span>
                {showWithdrawals ? "Hide" : "Show"} Withdrawals
                {pendingWithdrawals > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {pendingWithdrawals}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Withdrawal Management Section */}
        {showWithdrawals && (
          <div className="bg-white rounded-xl p-6 border shadow-lg mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                💸 Withdrawal Requests 
                {pendingWithdrawals > 0 && (
                  <span className="ml-2 px-2 py-1 bg-red-100 text-red-600 text-sm rounded-full">
                    {pendingWithdrawals} pending
                  </span>
                )}
              </h3>
              <p className="text-gray-600">Total: {withdrawals.length} requests</p>
            </div>
            
            <div className="space-y-4">
              {withdrawals.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">💸</div>
                  <p>No withdrawal requests found.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {withdrawals.map((withdrawal) => (
                    <div key={withdrawal.id} className="bg-gray-50 rounded-lg p-4 border">
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        {/* User Info */}
                        <div>
                          <p className="text-xs text-gray-500 font-medium">USER DETAILS</p>
                          <p className="font-semibold text-gray-800">{withdrawal.userName}</p>
                          <p className="text-sm text-gray-600">{withdrawal.userEmail}</p>
                          <p className="text-xs text-gray-500">
                            ID: {withdrawal.userId?.slice(-8)}
                          </p>
                        </div>

                        {/* Withdrawal Details */}
                        <div>
                          <p className="text-xs text-gray-500 font-medium">WITHDRAWAL INFO</p>
                          <p className="font-bold text-lg text-red-600">
                            {withdrawal.currency} {withdrawal.amount?.toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-600 capitalize">{withdrawal.requestType?.replace('_', ' ')}</p>
                          <span className={`inline-block px-2 py-1 rounded text-xs border ${getWithdrawalStatusColor(withdrawal.status)}`}>
                            {withdrawal.status?.toUpperCase()}
                          </span>
                        </div>

                        {/* Request Details */}
                        <div>
                          <p className="text-xs text-gray-500 font-medium">REQUEST DATE</p>
                          <p className="text-sm font-medium">
                            {withdrawal.requestedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {withdrawal.requestedAt?.toDate?.()?.toLocaleTimeString() || 'N/A'}
                          </p>
                          {withdrawal.processedAt && (
                            <p className="text-xs text-blue-600">
                              Processed: {withdrawal.processedAt.toDate().toLocaleDateString()}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          {withdrawal.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleWithdrawalAction(withdrawal.id, 'approved')}
                                disabled={processingWithdrawal === withdrawal.id}
                                className="px-3 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors disabled:opacity-50"
                              >
                                {processingWithdrawal === withdrawal.id ? "Processing..." : "✅ Approve"}
                              </button>
                              <button
                                onClick={() => handleWithdrawalAction(withdrawal.id, 'rejected', 'Rejected by admin')}
                                disabled={processingWithdrawal === withdrawal.id}
                                className="px-3 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors disabled:opacity-50"
                              >
                                ❌ Reject
                              </button>
                            </>
                          )}
                          
                          {withdrawal.status === 'approved' && (
                            <button
                              onClick={() => handleWithdrawalAction(withdrawal.id, 'completed', 'Payment sent to user')}
                              disabled={processingWithdrawal === withdrawal.id}
                              className="px-3 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
                            >
                              {processingWithdrawal === withdrawal.id ? "Processing..." : "✅ Mark Completed"}
                            </button>
                          )}
                          
                          {withdrawal.status === 'completed' && (
                            <span className="px-3 py-2 bg-gray-100 text-gray-600 text-sm rounded text-center">
                              ✅ Completed
                            </span>
                          )}
                          
                          {withdrawal.status === 'rejected' && (
                            <span className="px-3 py-2 bg-red-100 text-red-600 text-sm rounded text-center">
                              ❌ Rejected
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Enhanced Information */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        {/* User Settings Snapshot */}
                        {withdrawal.userSettingsSnapshot && (
                          <div className="mb-3 p-3 bg-blue-50 rounded border">
                            <p className="text-xs text-blue-600 font-medium mb-2">USER SETTINGS AT REQUEST TIME</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                              <div>
                                <span className="text-gray-500">Rate:</span>
                                <span className="ml-1 font-medium">
                                  {withdrawal.userSettingsSnapshot.monthlyRate || 'Global'}%
                                  {withdrawal.userSettingsSnapshot.isUsingGlobalRate && ' (Global)'}
                                </span>
                              </div>
                              {withdrawal.userSettingsSnapshot.referralBonus > 0 && (
                                <div>
                                  <span className="text-gray-500">Referral:</span>
                                  <span className="ml-1 font-medium">+{withdrawal.userSettingsSnapshot.referralBonus}%</span>
                                </div>
                              )}
                              {withdrawal.userSettingsSnapshot.hasActivePromos && (
                                <div>
                                  <span className="text-gray-500">Promos:</span>
                                  <span className="ml-1 font-medium text-purple-600">Active</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {withdrawal.bankDetails && (
                            <div>
                              <p className="text-xs text-gray-500 font-medium mb-1">BANK DETAILS</p>
                              <p className="text-gray-700 bg-gray-100 p-2 rounded text-xs font-mono">
                                {withdrawal.bankDetails}
                              </p>
                            </div>
                          )}
                          
                          {withdrawal.reason && (
                            <div>
                              <p className="text-xs text-gray-500 font-medium mb-1">REASON</p>
                              <p className="text-gray-700">{withdrawal.reason}</p>
                            </div>
                          )}
                        </div>

                        {/* Portfolio Snapshot */}
                        {withdrawal.eligibilitySnapshot && (
                          <div className="mt-3 p-3 bg-blue-50 rounded border">
                            <p className="text-xs text-blue-600 font-medium mb-2">PORTFOLIO SNAPSHOT AT REQUEST TIME</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                              <div>
                                <span className="text-gray-500">Capital:</span>
                                <span className="ml-1 font-medium">{withdrawal.currency} {withdrawal.eligibilitySnapshot.totalCapital?.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Value:</span>
                                <span className="ml-1 font-medium">{withdrawal.currency} {withdrawal.eligibilitySnapshot.totalCurrentValue?.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Profit:</span>
                                <span className="ml-1 font-medium">{withdrawal.currency} {withdrawal.eligibilitySnapshot.totalProfit?.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Available:</span>
                                <span className="ml-1 font-medium">{withdrawal.currency} {(withdrawal.eligibilitySnapshot.availableProfit + withdrawal.eligibilitySnapshot.availableCapital)?.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {withdrawal.adminNotes && (
                          <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                            <p className="text-xs text-yellow-600 font-medium">ADMIN NOTES</p>
                            <p className="text-sm text-yellow-800">{withdrawal.adminNotes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Management */}
        <div className="bg-white rounded-xl p-6 border shadow-lg mb-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-gray-800">👥 User Management</h3>
            <div className="flex gap-4">
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="space-y-3">
              {filteredUsers.map((user) => (
                <div
                  key={user.uid}
                  className="bg-gray-50 rounded-lg p-4 border hover:bg-gray-100 transition-colors"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold text-gray-800">{user.fullName}</h4>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      <p className="text-xs text-gray-500">ID: {user.uid.slice(-8)}</p>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Currency: {user.currency}</p>
                      </div>
                      
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      >
                        Manage User
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {filteredUsers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? "No users found matching your search." : "No users found."}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Deposits List */}
        <div className="bg-white rounded-xl p-6 border shadow-lg">
          <h3 className="text-lg font-bold text-gray-800 mb-4">📊 Recent Deposits</h3>
          
          <div className="overflow-x-auto">
            <div className="space-y-3">
              {deposits.slice(0, 20).map((deposit) => {
                // Try enhanced calculation first, fallback to legacy
                let stats = null;
                if (deposit.userId) {
                  // This will be calculated asynchronously, so we'll show loading state
                  stats = { currentValue: deposit.amount, totalGain: 0, gainPercentage: 0 };
                } else {
                  stats = calculateDepositStats(deposit, globalRate);
                }

                return (
                  <div
                    key={deposit.id}
                    className="bg-gray-50 rounded-lg p-4 border"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Deposit ID</p>
                        <p className="font-mono text-sm">#{deposit.id.slice(-8)}</p>
                        <p className="text-xs text-gray-500">
                          {deposit.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-xs text-gray-500">Amount</p>
                        <p className="font-bold text-gray-800">
                          {deposit.currency} {deposit.amount?.toLocaleString() || '0'}
                        </p>
                        <span className={`inline-block px-2 py-1 rounded text-xs ${
                          deposit.status === 'confirmed' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {deposit.status}
                        </span>
                      </div>
                      
                      <div>
                        {stats && deposit.status === 'confirmed' ? (
                          <>
                            <p className="text-xs text-gray-500">Current Value</p>
                            <p className="text-green-600 font-bold">
                              {deposit.currency} {stats.currentValue.toFixed(2).toLocaleString()}
                            </p>
                          </>
                        ) : (
                          <p className="text-gray-500">Pending confirmation</p>
                        )}
                      </div>
                      
                      <div>
                        {stats && deposit.status === 'confirmed' && (
                          <>
                            <p className="text-xs text-gray-500">Growth</p>
                            <p className="text-green-600 font-bold">
                              +{stats.gainPercentage.toFixed(2)}%
                            </p>
                            <p className="text-green-600 text-xs">
                              +{deposit.currency} {stats.totalGain.toFixed(2)}
                            </p>
                          </>
                        )}
                      </div>
                    </div>

                    {deposit.description && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-gray-500">Description: {deposit.description}</p>
                      </div>
                    )}

                    {deposit.metadata?.enhancedSystem && (
                      <div className="mt-2">
                        <span className="inline-block px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                          Enhanced System
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

              {deposits.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No deposits found.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modals */}
        {selectedUser && (
          <UserManagementModal
            user={selectedUser}
            onClose={() => setSelectedUser(null)}
            onSuccess={handleSuccess}
          />
        )}

        {/* Create Deposit Modal */}
        {showCreateForm && (
          <CreateDepositForm
            onClose={() => setShowCreateForm(false)}
            onSuccess={handleSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default EnhancedAdminDashboard;